import {Database} from '@tnc/shared';
import React from 'react';

import {ColumnSelectionT} from '@/lib/types/query.types';

export * from './defaultDeleteData';
export * from './defaultUpdateData';
export * from './getCreateProductFields';
export * from './defaultCreateData';
export * from './removeMenuTitle';
export * from './queryKeys';
export * from './formatters';

/**
 * Function to convert columns to a string.
 * @param {ColumnSelectionT} columns - The columns to convert.
 * @returns {string} - The string of columns.
 */
export function convertColumnsToString<T extends keyof Database['public']['Tables']>(
  columns: ColumnSelectionT<T>[]
): string {
  return columns
    .map(column => {
      if (typeof column === 'string') {
        return column;
      } else if (Array.isArray(column)) {
        const [relation, innerColumns] = column;
        const innerColumnsStr = convertColumnsToString<T>(innerColumns as ColumnSelectionT<T>[]);
        return `${relation}(${innerColumnsStr})`;
      }
      return '';
    })
    .join(',');
}

/**
 * Helper function to lazy load a component, whether it's a default export or a named export.
 *
 * @param importFunc - A function that dynamically imports the module.
 * @param exportName - (Optional) The name of the export to load. If not provided, defaults to 'default'.
 * @returns A React component that can be used with React.lazy().
 */
export function lazyLoadComponent<T extends {[key: string]: React.ComponentType<any>}>(
  importFunc: () => Promise<T>,
  exportName?: keyof T
): React.LazyExoticComponent<React.ComponentType<any>> {
  return React.lazy(() =>
    importFunc().then(module => {
      const component = exportName ? module[exportName] : module.default;
      if (!component) {
        throw new Error(
          `Cannot find ${exportName ? `named export '${String(exportName)}'` : 'default export'} in module.`
        );
      }
      return {default: component};
    })
  );
}
